import moment from "moment-timezone";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import React, { useContext, useState } from "react";
import { useTranslation } from "@i18n/hooks";
import { AllFileTypeOptions, type FileTypeOption } from "./file-filters.const";
import { CertificateTag, FilesSearchContext } from "../FileSearchContainer";

enum ShowFilesMode {
	All,
	AllButCertificates,
	OnlyCertificates,
}

export type FileFiltersProps = {
	fileTypeOptions?: FileTypeOption[];
};

export const FileFilters = ({ fileTypeOptions = AllFileTypeOptions }: FileFiltersProps) => {
	const { t } = useTranslation("common");
	const { filters, setFilters, clearFilters } = useContext(FilesSearchContext);
	const defaultCertificateFilter = ShowFilesMode.AllButCertificates;
	const [showFilesMode, setShowFilesMode] = useState(defaultCertificateFilter);

	return (
		<div className="flex mb-3 gap-2">
			<InputText
				value={filters.filterByName ?? ""}
				onChange={(e) => {
					setFilters({
						...filters,
						filterByName: e.target.value,
					});
				}}
				placeholder={t("files.name")}
			/>

			<MultiSelect
				value={filters.filterByFileTypes}
				onChange={(e) => {
					setFilters({
						...filters,
						filterByFileTypes: e.target.value,
					});
				}}
				options={fileTypeOptions}
				showSelectAll={false}
				placeholder={t("files.type")}
			/>

			<div className="flex align-items-center">
				<span className="mr-1">{t("files.certificates")}:</span>
				<Dropdown
					value={showFilesMode}
					options={[
						{ label: t("files.showWith"), value: ShowFilesMode.All },
						{ label: t("files.dontShow"), value: ShowFilesMode.AllButCertificates },
						{ label: t("files.showOnly"), value: ShowFilesMode.OnlyCertificates },
					]}
					onChange={(e) => {
						const showMode: ShowFilesMode = e.target.value;
						setShowFilesMode(showMode);
						const issuedCertificateTags = [CertificateTag];
						if (showMode === ShowFilesMode.OnlyCertificates) {
							setFilters({
								...filters,
								filterFileTagsIncluded: issuedCertificateTags,
								filterFileTagsExcluded: [],
							});
						} else if (showMode === ShowFilesMode.AllButCertificates) {
							setFilters({
								...filters,
								filterFileTagsIncluded: undefined,
								filterFileTagsExcluded: issuedCertificateTags,
							});
						} else {
							setFilters({
								...filters,
								filterFileTagsIncluded: undefined,
								filterFileTagsExcluded: [],
							});
						}
					}}
				/>
			</div>

			<Calendar
				showTime
				hourFormat="24"
				dateFormat={"dd.mm.yy"}
				placeholder={t("files.createdFrom")}
				value={
					filters.filterByFromDateTimeInclusive
						? moment(
								filters.filterByFromDateTimeInclusive.replace("[UTC]", ""),
						  ).toDate()
						: undefined
				}
				onChange={(e) => {
					setFilters({
						...filters,
						filterByFromDateTimeInclusive: e.value
							? moment(e.value as Date).format("YYYY-MM-DDTHH:mmZ")
							: undefined,
					});
				}}
			/>

			<Calendar
				showTime
				hourFormat="24"
				dateFormat={"dd.mm.yy"}
				placeholder={t("files.createdTo")}
				value={
					filters.filterByToDateTimeInclusive
						? moment(filters.filterByToDateTimeInclusive.replace("[UTC]", "")).toDate()
						: undefined
				}
				onChange={(e) => {
					setFilters({
						...filters,
						filterByToDateTimeInclusive: e.value
							? moment(e.value as Date).format("YYYY-MM-DDTHH:mmZ")
							: undefined,
					});
				}}
			/>

			<div className="flex align-items-center">
				<Button
					className=""
					label={t("files.reset")}
					icon="pi pi-times"
					onClick={() => {
						clearFilters();
						setShowFilesMode(defaultCertificateFilter);
					}}
				/>
			</div>
		</div>
	);
};
